import { SlideGroup } from '../Builder/CreateTransistionGroup/utils';

type Id = number | string;

// Structural so this module stays free of the store's types (useSlideShowStore imports it).
interface SlideLike {
  id: Id;
  position: number;
  slideGroupId?: number | null;
}

const byPosition = <T extends { position: number }>(items: T[]) => [...items].sort((a, b) => a.position - b.position);

const groupKey = (slide: SlideLike, knownGroupIds: Set<string>) => {
  if (slide.slideGroupId == null) return null;

  const id = String(slide.slideGroupId);

  return knownGroupIds.has(id) ? id : null;
};

// The order the sidebar renders groups in: by their first slide's position. Groups without slides follow, by
// slide_groups.position, since formatPresentSlides never injects a card for them.
export const orderedGroupIds = (slides: SlideLike[], slideGroups: SlideGroup[]): string[] => {
  const knownGroupIds = new Set(slideGroups.map(group => String(group.id)));
  const seen: string[] = [];

  byPosition(slides).forEach((slide) => {
    const key = groupKey(slide, knownGroupIds);
    if (key && !seen.includes(key)) seen.push(key);
  });

  const slideless = byPosition(slideGroups.filter(group => !seen.includes(String(group.id))));

  return [...seen, ...slideless.map(group => String(group.id))];
};

export const moveGroup = (orderedIds: string[], groupId: Id, targetPosition: number): string[] => {
  const id = String(groupId);
  const without = orderedIds.filter(existing => existing !== id);
  if (without.length === orderedIds.length) return orderedIds;

  const index = Math.min(Math.max(targetPosition, 1), orderedIds.length) - 1;
  without.splice(index, 0, id);

  return without;
};

// Mirrors SlideGroupReorderService: ungrouped slides first (a slide pointing at an unknown group counts as
// ungrouped), then each group's slides as one block in the requested group order, renumbered 1..N.
export const reorderSlidesByGroupOrder = <T extends SlideLike>(
  slides: T[],
  slideGroups: SlideGroup[],
  orderedIds: string[],
): T[] => {
  const knownGroupIds = new Set(slideGroups.map(group => String(group.id)));
  const sorted = byPosition(slides);
  const missing = [...knownGroupIds].filter(id => !orderedIds.includes(id));
  const groupOrder = [...orderedIds, ...missing];

  const ungrouped = sorted.filter(slide => groupKey(slide, knownGroupIds) === null);
  const grouped = groupOrder.flatMap(id => sorted.filter(slide => groupKey(slide, knownGroupIds) === id));

  return [...ungrouped, ...grouped].map((slide, index) => ({ ...slide, position: index + 1 }));
};

export const withGroupPositions = (slideGroups: SlideGroup[], orderedIds: string[]): SlideGroup[] => {
  const rank = (group: SlideGroup) => {
    const index = orderedIds.indexOf(String(group.id));

    return index === -1 ? orderedIds.length : index;
  };

  return [...slideGroups]
    .sort((a, b) => rank(a) - rank(b))
    .map((group, index) => ({ ...group, position: index + 1 }));
};
